import type { Socket } from "socket.io-client";
import { SocketContext } from "./socketContextState";
// SocketContext.tsx
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import { Button } from "@/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { createSocket, disconnectSocket } from "../lib/socket";
import { getAccessToken, onAccessTokenChange } from "../lib/tokenService";
import { getInitials } from "@/lib/utils";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert } from "lucide-react";
import {
  applyMessageDeliveryToPortalCaches,
  applyPortalMessageChangedEvent,
  applySocketMessageToPortalCaches,
  clearInboxUnreadCountByConversationId,
  clearSocketReadStateFromPortalCaches,
} from "@/hooks/portalMessages/portalMessageCache";
import { applyAddOnAttentionToActivityCaches, applySocketActivityToActivityCaches } from "@/hooks/activity/activityQueryCache";
import { applyCheckoutToActivityCaches } from "@/hooks/activity/activityQueryCache";
import type { CheckoutEvent } from "@/types/attendance";
import { activityQueryKeys } from "@/hooks/activity/useActivity";
import { formatCalendarDate } from "@/lib/utils";
import { portalMessageQueryKeys } from "@/hooks/portalMessages/portalMessageQueryKeys";
import type { ActivityDayLog, AddOnAttention } from "@/types/activity";
import type { ProfileAvatar } from "@/types/avatar";
import type {
  GymMessagesReadEvent,
  PortalMessageDeliveredEvent,
  PortalMessageReadEvent,
  PortalMessage,
  PortalMessageChangedEvent,
  PortalNewMessageEvent,
} from "@/types/portalMessages";

interface DecodedToken {
  fitnessCenterId: string;
  // Add other token fields as needed
}

interface SocketProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
  connectDelayMs?: number;
}

const CHECK_IN_TOAST_DURATION = 8_000;
const ATTENTION_WAIT_MS = 2_000;
const CHECK_IN_RECORD_MS = 30_000;

interface CheckInToastMember {
  name: string;
  avatar?: ProfileAvatar | null;
}

interface GymCheckInEvent {
  success: boolean;
  message: string;
  activity?: ActivityDayLog;
  member?: {
    memberId: string;
    name: string;
    avatar?: ProfileAvatar | null;
  };
}

interface GymAddOnAttentionEvent {
  member: { memberId: string; name: string; avatar?: ProfileAvatar | null };
  checkInId: string;
  addOnAttention: AddOnAttention[];
}

const CheckInToast = ({
  success,
  message,
  member,
  addOnAttention = [],
}: {
  success: boolean;
  message: string;
  member?: CheckInToastMember;
  addOnAttention?: AddOnAttention[];
}) => {
  const memberName = member?.name.trim();
  const avatarUrl =
    member?.avatar?.thumbnailUrl || member?.avatar?.url || undefined;

  return (
    <div
      className="w-[min(22rem,calc(100vw-2rem))] rounded-lg border bg-background p-3 shadow-lg"
      role="status"
    >
      <div className="flex items-center gap-3">
      {member ? (
        <Avatar className="h-10 w-10 shrink-0">
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={memberName || "Member avatar"} />
          ) : null}
          <AvatarFallback className="bg-primary/10 text-xs text-primary">
            {getInitials(memberName || "Member")}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            success
              ? "bg-success/10 text-success"
              : "bg-destructive/10 text-destructive"
          }`}
          aria-hidden="true"
        >
          {success ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <CircleAlert className="h-5 w-5" />
          )}
        </div>
      )}
      <div className="min-w-0">
        {memberName ? <p className="font-medium">{memberName}</p> : null}
        <p className="break-words text-sm text-muted-foreground">{message}</p>
      </div>
      </div>
      {addOnAttention.length > 0 && (
        <div className="mt-3 border-l-2 border-warning bg-warning/5 py-2 pl-3 pr-2 text-xs">
          <p className="font-semibold text-warning">Add-on attention</p>
          {addOnAttention.map((item) => (
            <p key={item.purchaseId} className="mt-1 break-words text-foreground">
              {item.name} {item.status === "expired" ? "expired" : "expires"} {formatCalendarDate(item.lastUsableDate, "d MMM")}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export const SocketProvider: React.FC<SocketProviderProps> = ({
  children,
  enabled = true,
  connectDelayMs = 150,
}) => {
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [fitnessCenterId, setFitnessCenterId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messageChangeListenerReady, setMessageChangeListenerReady] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const activeTokenRef = useRef<string | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledPortalMessageIdsRef = useRef<Set<string>>(new Set());
  const handledAttentionIdsRef = useRef<Map<string, string>>(new Map());
  const handledCheckInIdsRef = useRef<Set<string>>(new Set());
  const pendingAttentionRef = useRef<Map<string, AddOnAttention[]>>(new Map());
  const checkInToastRef = useRef<Map<string, {
    message: string;
    member?: CheckInToastMember;
    attention: AddOnAttention[];
    receivedAt: number;
  }>>(new Map());
  const pendingAttentionMemberRef = useRef<Map<string, CheckInToastMember>>(new Map());
  const attentionTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const fallbackAttentionIdsRef = useRef<Set<string>>(new Set());
  const successAudioRef = useRef<HTMLAudioElement | null>(null);
  const failAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(false);
  const [showEnableSound, setShowEnableSound] = useState(false);

  const resetAudio = (audio: HTMLAudioElement | null) => {
    if (!audio) {
      return;
    }

    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
  };

  const unlockCheckInAudio = useCallback(async () => {
    const audios = [successAudioRef.current, failAudioRef.current].filter(
      (audio): audio is HTMLAudioElement => audio !== null
    );

    if (audios.length === 0) {
      return;
    }

    try {
      await Promise.all(
        audios.map(async (audio) => {
          audio.muted = true;
          audio.currentTime = 0;
          await audio.play();
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
      );

      setIsSoundEnabled(true);
      setShowEnableSound(false);
    } catch (error) {
      audios.forEach((audio) => resetAudio(audio));
      console.warn("Unable to unlock portal check-in audio:", error);
      setIsSoundEnabled(false);
      setShowEnableSound(true);
    }
  }, []);

  const playCheckInAudio = useCallback(
    (isSuccess: boolean) => {
      const audio = isSuccess ? successAudioRef.current : failAudioRef.current;
      if (!audio) {
        return;
      }

      audio.currentTime = 0;
      void audio.play().catch((error) => {
        console.warn("Portal check-in sound playback was blocked:", error);
        setIsSoundEnabled(false);
        setShowEnableSound(true);
      });
    },
    []
  );

  /**
   * Decode token and extract fitness center ID
   */
  const decodeToken = useCallback((token: string): string | null => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      return decoded.fitnessCenterId;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }, []);

  /**
   * Handle token changes - connect or disconnect socket
   */
  const handleTokenChange = useCallback(
    (token: string | null) => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }

      if (!token) {
        // No token - disconnect socket
        disconnectSocket();
        socketRef.current = null;
        activeTokenRef.current = null;
        setSocket(null);
        setFitnessCenterId(null);
        setIsConnected(false);
        return;
      }

      // Extract fitness center ID from token
      const centerId = decodeToken(token);
      if (!centerId) {
        console.error("Invalid token - missing fitnessCenterId");
        return;
      }

      setFitnessCenterId(centerId);

      if (activeTokenRef.current === token && socketRef.current) {
        return;
      }

      activeTokenRef.current = token;

      // Create new socket with token and connect
      const socketInstance = createSocket(token);
      if (socketInstance) {
        socketRef.current = socketInstance;
        const connect = () => {
          socketInstance.connect();
          connectTimeoutRef.current = null;
        };

        if (connectDelayMs > 0) {
          connectTimeoutRef.current = setTimeout(connect, connectDelayMs);
        } else {
          connect();
        }

        setSocket(socketInstance);
        setIsConnected(socketInstance.connected);
      }
    },
    [connectDelayMs, decodeToken]
  );

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(() => {
    const token = getAccessToken();
    if (token) {
      handleTokenChange(token);
    }
  }, [handleTokenChange]);

  /**
   * Setup socket connection on mount and listen for token changes
   */
  useEffect(() => {
    if (!enabled) {
      handleTokenChange(null);
      return;
    }

    // Try to connect with initial token
    const initialToken = getAccessToken();
    if (initialToken) {
      handleTokenChange(initialToken);
    }

    // Subscribe to token changes
    const unsubscribe = onAccessTokenChange(handleTokenChange);

    // Cleanup on unmount
    return () => {
      unsubscribe();
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      disconnectSocket();
      socketRef.current = null;
      activeTokenRef.current = null;
    };
  }, [enabled, handleTokenChange]);

  useEffect(() => {
    if (typeof Audio === "undefined") {
      return;
    }

    const successAudio = new Audio("/sounds/check-in-success.mp3");
    const failAudio = new Audio("/sounds/check-in-fail.mp3");

    successAudio.preload = "auto";
    failAudio.preload = "auto";

    successAudioRef.current = successAudio;
    failAudioRef.current = failAudio;

    return () => {
      resetAudio(successAudioRef.current);
      resetAudio(failAudioRef.current);
      successAudioRef.current = null;
      failAudioRef.current = null;
      setIsSoundEnabled(false);
      setShowEnableSound(false);
    };
  }, []);

  useEffect(() => {
    if (isSoundEnabled || typeof window === "undefined") {
      return;
    }

    const handleFirstInteraction = () => {
      void unlockCheckInAudio();
    };

    const listenerOptions = { once: true, passive: true } as const;

    window.addEventListener("pointerdown", handleFirstInteraction, listenerOptions);
    window.addEventListener("keydown", handleFirstInteraction, listenerOptions);
    window.addEventListener("touchstart", handleFirstInteraction, listenerOptions);

    return () => {
      window.removeEventListener("pointerdown", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
    };
  }, [isSoundEnabled, unlockCheckInAudio]);

  /**
   * Listen to socket connection state changes
  */
  useEffect(() => {
    if (!socket) {
      setMessageChangeListenerReady(false);
      return;
    }
    const attentionTimers = attentionTimerRef.current;
    const pendingAttention = pendingAttentionRef.current;
    const pendingMembers = pendingAttentionMemberRef.current;
    const checkInToasts = checkInToastRef.current;
    const fallbackIds = fallbackAttentionIdsRef.current;
    const handledAttention = handledAttentionIdsRef.current;
    const handledCheckIns = handledCheckInIdsRef.current;

    const handleConnect = () => {
      setIsConnected(true);
      socket.emit("registerGymPortalV2", { fitnessCenterId });
      socket.emit("registerAttendanceRoom");
      void queryClient.invalidateQueries({ queryKey: ["attendanceToday"] });
      void queryClient.invalidateQueries({ queryKey: ["checkInHistory"] });
      void queryClient.invalidateQueries({ queryKey: activityQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: portalMessageQueryKeys.threads });
      void queryClient.invalidateQueries({ queryKey: portalMessageQueryKeys.conversations });
      void queryClient.invalidateQueries({
        queryKey: [...portalMessageQueryKeys.all, "history"],
      });
      console.log(`Socket joined room: center-${fitnessCenterId}`);
    };
    const handleDisconnect = () => setIsConnected(false);

    const toastKey = (checkInId: string) => `${fitnessCenterId}:${checkInId}`;
    const showCheckInToast = (key: string, record: NonNullable<ReturnType<typeof checkInToastRef.current.get>>) => {
      toast.custom(
        () => <CheckInToast success message={record.message} member={record.member} addOnAttention={record.attention} />,
        { id: `check-in:${key}`, duration: CHECK_IN_TOAST_DURATION },
      );
    };
    const clearAttentionTimer = (key: string) => {
      const timer = attentionTimerRef.current.get(key);
      if (timer) clearTimeout(timer);
      attentionTimerRef.current.delete(key);
    };
    const pruneCheckInRecords = () => {
      const cutoff = Date.now() - CHECK_IN_RECORD_MS;
      for (const [key, record] of checkInToastRef.current) {
        if (record.receivedAt < cutoff) checkInToastRef.current.delete(key);
      }
    };

    // 2. Event logic (Things to LISTEN for globally)
    const handleCheckIn = (data: GymCheckInEvent) => {
      const checkInKey = data.success && data.activity?.id ? toastKey(data.activity.id) : null;
      if (checkInKey && handledCheckInIdsRef.current.has(checkInKey)) return;
      if (checkInKey) {
        handledCheckInIdsRef.current.add(checkInKey);
        if (handledCheckInIdsRef.current.size > 200) {
          const oldest = handledCheckInIdsRef.current.values().next().value;
          if (oldest) handledCheckInIdsRef.current.delete(oldest);
        }
      }
      console.log("Global check-in received:", data);
      playCheckInAudio(data.success);
      const member = data.success
        ? data.activity
          ? { name: data.activity.member, avatar: data.activity.avatar }
          : undefined
        : data.member;

      const key = checkInKey;
      const attention = key ? pendingAttentionRef.current.get(key) ?? [] : [];
      if (key) {
        clearAttentionTimer(key);
        if (fallbackAttentionIdsRef.current.delete(key)) toast.dismiss(`add-on-attention:${key}`);
        pendingAttentionRef.current.delete(key);
        pendingAttentionMemberRef.current.delete(key);
        pruneCheckInRecords();
        const record = { message: data.message, member, attention, receivedAt: Date.now() };
        checkInToastRef.current.set(key, record);
        if (checkInToastRef.current.size > 200) {
          const oldest = checkInToastRef.current.keys().next().value;
          if (oldest) checkInToastRef.current.delete(oldest);
        }
        showCheckInToast(key, record);
      } else {
        toast.custom(
          () => <CheckInToast success={data.success} message={data.message} member={member} />,
          { duration: CHECK_IN_TOAST_DURATION },
        );
      }

      if (data.success) {
        void queryClient.invalidateQueries({ queryKey: ["attendanceToday"] });
        void queryClient.invalidateQueries({ queryKey: ["checkInHistory"] });
        void queryClient.invalidateQueries({ queryKey: ["membersData"] });
        if (data.activity) {
          applySocketActivityToActivityCaches(queryClient,
            attention.length ? { ...data.activity, addOnAttention: attention } : data.activity);
        }
      }
    };

    const handleAttendanceChanged = (event: { fitnessCenterId: string }) => {
      if (event.fitnessCenterId !== fitnessCenterId) return;
      void queryClient.invalidateQueries({ queryKey: ["attendanceToday"] });
      void queryClient.invalidateQueries({ queryKey: ["checkInHistory"] });
      void queryClient.invalidateQueries({ queryKey: ["membersData"] });
      void queryClient.invalidateQueries({ queryKey: activityQueryKeys.all });
    };
    const handleCheckout = (event: CheckoutEvent) => {
      if (event.fitnessCenterId !== fitnessCenterId) return;
      applyCheckoutToActivityCaches(queryClient, event);
      void queryClient.invalidateQueries({ queryKey: ["attendanceToday"] });
      void queryClient.invalidateQueries({ queryKey: ["checkInHistory"] });
    };
    const handleAddOnAttention = (data: GymAddOnAttentionEvent) => {
      if (!data.checkInId) return;
      const key = toastKey(data.checkInId);
      const signature = JSON.stringify(data.addOnAttention);
      if (handledAttentionIdsRef.current.get(key) === signature) return;
      handledAttentionIdsRef.current.set(key, signature);
      if (handledAttentionIdsRef.current.size > 200) {
        const first = handledAttentionIdsRef.current.keys().next().value;
        if (first) handledAttentionIdsRef.current.delete(first);
      }
      const record = checkInToastRef.current.get(key);
      const previous = record?.attention ?? pendingAttentionRef.current.get(key) ?? [];
      const merged = [...new Map([...previous, ...data.addOnAttention].map((item) => [item.purchaseId, item])).values()];
      applyAddOnAttentionToActivityCaches(queryClient, data.checkInId, merged);
      if (record) {
        record.attention = merged;
        record.receivedAt = Date.now();
        showCheckInToast(key, record);
      } else {
        pendingAttentionRef.current.set(key, merged);
        pendingAttentionMemberRef.current.set(key, data.member);
        if (!attentionTimerRef.current.has(key) && !fallbackAttentionIdsRef.current.has(key)) {
          const timer = setTimeout(() => {
            attentionTimerRef.current.delete(key);
            const items = pendingAttentionRef.current.get(key);
            if (!items?.length || checkInToastRef.current.has(key)) return;
            fallbackAttentionIdsRef.current.add(key);
            toast.custom(
              () => <CheckInToast success message="Add-on attention for recent check-in" member={pendingAttentionMemberRef.current.get(key)} addOnAttention={items} />,
              { id: `add-on-attention:${key}`, duration: CHECK_IN_TOAST_DURATION },
            );
          }, ATTENTION_WAIT_MS);
          attentionTimerRef.current.set(key, timer);
        } else if (fallbackAttentionIdsRef.current.has(key)) {
          toast.custom(
            () => <CheckInToast success message="Add-on attention for recent check-in" member={data.member} addOnAttention={merged} />,
            { id: `add-on-attention:${key}`, duration: CHECK_IN_TOAST_DURATION },
          );
        }
      }
      if (pendingAttentionRef.current.size > 200) {
        const first = pendingAttentionRef.current.keys().next().value;
        if (first) {
          pendingAttentionRef.current.delete(first);
          pendingAttentionMemberRef.current.delete(first);
          fallbackAttentionIdsRef.current.delete(first);
          clearAttentionTimer(first);
        }
      }
      void queryClient.invalidateQueries({ queryKey: activityQueryKeys.lists() });
    };

    const applyPortalMessage = (message: PortalMessage) => {
      if (handledPortalMessageIdsRef.current.has(message._id)) {
        return;
      }

      handledPortalMessageIdsRef.current.add(message._id);
      const conversationExists = applySocketMessageToPortalCaches(
        queryClient,
        message,
      );

      if (!conversationExists) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.conversations,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.unreadCount(),
      });
    };

    const handleNewGymMessage = (event: PortalNewMessageEvent | PortalMessage) => {
      const message = "message" in event ? event.message : event;
      if (!message?._id || !message.memberId) return;
      applyPortalMessage(message);
    };

    const handleMessageRead = (event: PortalMessageReadEvent) => {
      if (event.readBy !== "gym" && event.readBy !== "staff") {
        return;
      }

      clearInboxUnreadCountByConversationId(queryClient, event.conversationId);
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.unreadCount(),
      });
    };

    const handleMessageDelivered = (event: PortalMessageDeliveredEvent) => {
      const membersNeedingRefresh = applyMessageDeliveryToPortalCaches(
        queryClient,
        event.conversationId,
        event.messageId,
        event.clientMessageId,
      );
      for (const memberId of membersNeedingRefresh) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.thread(memberId),
        });
      }
    };

    const handleGymMessagesRead = (event: GymMessagesReadEvent) => {
      if (event.readBy !== "gym") {
        return;
      }

      clearSocketReadStateFromPortalCaches(queryClient, event.memberId);
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.unreadCount(),
      });
    };

    const handleGymMessageChanged = (event: PortalMessageChangedEvent) => {
      const result = applyPortalMessageChangedEvent(queryClient, event);
      if (!result.applied) return;

      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.messageHistory(event.message._id),
      });

      if (!result.threadUpdated) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.thread(event.message.memberId),
        });
      }
      if (!result.conversationUpdated) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.conversations,
        });
      }
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("gym-attendance-changed", handleAttendanceChanged);
    socket.on("gym-check-out", handleCheckout);
    socket.on("gym-check-in", handleCheckIn); // <--- Listen here
    socket.on("gym-add-on-attention", handleAddOnAttention);
    socket.on("newGymMessageV2", handleNewGymMessage);
    socket.on("messageReadV2", handleMessageRead);
    socket.on("messageDeliveredV2", handleMessageDelivered);
    socket.on("gymMessagesReadV2", handleGymMessagesRead);
    socket.on("gymMessageChanged", handleGymMessageChanged);
    setMessageChangeListenerReady(true);

    // Set initial state
    setIsConnected(socket.connected);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("gym-attendance-changed", handleAttendanceChanged);
      socket.off("gym-check-out", handleCheckout);
      socket.off("gym-check-in", handleCheckIn); // <--- Listen here
      socket.off("gym-add-on-attention", handleAddOnAttention);
      for (const timer of attentionTimers.values()) clearTimeout(timer);
      attentionTimers.clear();
      pendingAttention.clear();
      pendingMembers.clear();
      checkInToasts.clear();
      fallbackIds.clear();
      handledAttention.clear();
      handledCheckIns.clear();
      socket.off("newGymMessageV2", handleNewGymMessage);
      socket.off("messageReadV2", handleMessageRead);
      socket.off("messageDeliveredV2", handleMessageDelivered);
      socket.off("gymMessagesReadV2", handleGymMessagesRead);
      socket.off("gymMessageChanged", handleGymMessageChanged);
      setMessageChangeListenerReady(false);
    };
  }, [fitnessCenterId, playCheckInAudio, queryClient, socket]);

  return (
    <SocketContext.Provider
      value={{ socket, fitnessCenterId, isConnected, messageChangeListenerReady, reconnect }}
    >
      {children}
      {showEnableSound && !isSoundEnabled ? (
        <div className="fixed bottom-4 right-4 z-50 max-w-xs rounded-lg border bg-background/95 p-4 shadow-lg backdrop-blur">
          <p className="text-sm font-medium">Enable check-in sound</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Browsers can block audio until someone interacts with the page once.
          </p>
          <Button className="mt-3 w-full" onClick={() => void unlockCheckInAudio()}>
            Enable sound
          </Button>
        </div>
      ) : null}
    </SocketContext.Provider>
  );
};

